import StaticPage from "../components/StaticPage";
import useSEO from "../hooks/useSEO";

export default function PrivacyPage() {
  useSEO({
    title: "Privacy Policy | GetDeparto",
    description:
      "How GetDeparto collects, uses, stores, and protects your information when you order a temporary flight reservation. Includes data retention and your rights.",
    canonical: "https://getdeparto.com/privacy",
    og: {
      title: "Privacy Policy | GetDeparto",
      description:
        "How GetDeparto collects, uses, and protects your information when you order a flight reservation.",
      url: "https://getdeparto.com/privacy",
      image: "https://getdeparto.com/og-image.png",
      type: "website",
      siteName: "GetDeparto",
    },
  });
  return (
    <StaticPage
      title="Privacy Policy"
      subtitle="This policy explains what information we collect, why we collect it, and what choices you have."
      updated="April 2026"
    >
      <section>
        <h2>1. Information we collect</h2>
        <p>
          When you place an order on getdeparto, we collect the details needed
          to create and deliver your reservation:
        </p>
        <ul>
          <li>Full name (as per your passport)</li>
          <li>Email address</li>
          <li>WhatsApp / phone number</li>
          <li>Travel details - origin, destination, date, trip type, passengers</li>
          <li>Payment transaction metadata (we do not store card or UPI details)</li>
        </ul>
      </section>

      <section>
        <h2>2. How we use your information</h2>
        <ul>
          <li>To create and deliver your flight reservation</li>
          <li>
            To send order confirmations, itinerary files, and validity
            reminders
          </li>
          <li>To provide customer support, reissues, or refunds</li>
          <li>To comply with legal and tax obligations in India</li>
        </ul>
      </section>

      <section>
        <h2>3. How we share your information</h2>
        <ul>
          <li>
            <strong>Travel partners and booking systems</strong> - we share the
            minimum details required (passenger name, route, date) to create
            your reservation.
          </li>
          <li>
            <strong>Payment processors</strong> - payment is handled by
            reputable third-party gateways; they receive only the information
            needed to process your transaction.
          </li>
          <li>
            <strong>Communication providers</strong> - WhatsApp and email
            providers we use to deliver your itinerary.
          </li>
          <li>
            We <strong>do not sell</strong> your personal information to anyone.
          </li>
        </ul>
      </section>

      <section>
        <h2>4. Security</h2>
        <p>
          We apply reasonable technical and organisational measures to protect
          the information you share with us, including encrypted connections
          (HTTPS), access controls, and limited staff access to order data. No
          online service can be 100% secure, so we also encourage you to use a
          strong, unique password on your email account.
        </p>
      </section>

      <section>
        <h2>5. Data retention</h2>
        <p>
          We retain order records only as long as necessary to provide the
          service, support reissues and refunds, and comply with applicable tax
          and legal obligations. Communication logs older than 24 months are
          typically purged or anonymised.
        </p>
      </section>

      <section>
        <h2>6. Your rights</h2>
        <ul>
          <li>
            <strong>Access</strong> - request a copy of the personal
            information we hold about you.
          </li>
          <li>
            <strong>Correction</strong> - request that we fix inaccurate
            details.
          </li>
          <li>
            <strong>Deletion</strong> - request that we delete your personal
            information, subject to any legal retention obligations.
          </li>
        </ul>
        <p>
          To exercise any of these rights, email us at{" "}
          <a className="gd-link" href="mailto:support@getdeparto.com">
            support@getdeparto.com
          </a>
          . We will respond within a reasonable timeframe.
        </p>
      </section>

      <section>
        <h2>7. Updates to this policy</h2>
        <p>
          We may update this Privacy Policy as our service evolves or when
          required by law. The latest version will always be available on this
          page with a new "Last updated" date.
        </p>
      </section>

      <section>
        <h2>8. Contact</h2>
        <p>
          Get Departo · Bengaluru, India
          <br />
          Email:{" "}
          <a className="gd-link" href="mailto:support@getdeparto.com">
            support@getdeparto.com
          </a>
          <br />
          WhatsApp:{" "}
          <a className="gd-link" href="https://wa.me/919036705707">
            +91 90367 05707
          </a>
        </p>
      </section>
    </StaticPage>
  );
}
